import { DoughnutOption, ProgressBarOption } from '@erm/cluster';
import {
  DashboardPinnedItem,
  DownloadProgressArgs,
  DownloadProgressReading,
  OutcomeDocument,
  OutcomeDownloadFailureReason,
  OutcomeDownloadOutcome,
  SubmissionChartMapper,
  SubmissionStatus,
  SubmissionStageChartTooltipEntry,
  SubmissionStageView,
} from '../types';

import {
  DASHBOARD_SUBMISSION_STATUS_COLORS,
  DEFAULT_DONUT_SUBTITLE_STYLE,
  DEFAULT_DONUT_TEXT_STYLE,
  DEFAULT_SUBMISSION_CHART_GRID,
  OUTCOME_READY_PROGRESS_CEILING,
  SUBMISSION_STATUS_LABEL_MAPPER,
} from '../constants';

function buildSubmissionChartTextGraphic(
  text: string,
  top: string,
  fontSize: number,
  style: Record<string, string | number | 'center'> = DEFAULT_DONUT_TEXT_STYLE,
  z?: number,
) {
  return {
    type: 'text',
    left: 'center',
    top,
    ...(z !== undefined ? { z } : {}),
    style: {
      ...style,
      text,
      fontSize,
    },
  };
}

export function mapSubmissionStatusColor(
  submission: SubmissionStatus,
  loadDefaultState = false,
) {
  const _submissionChartData = {
    name: SUBMISSION_STATUS_LABEL_MAPPER.get(submission.status.toLowerCase()),
    value: submission.count,
  };
  return {
    ..._submissionChartData,
    itemStyle: {
      color: DASHBOARD_SUBMISSION_STATUS_COLORS.get(
        loadDefaultState ? 'Default' : submission.status,
      ),
    },
  };
}

export function calcMinAngle(submission: SubmissionStatus[]): number {
  if (!submission || !submission.length) return 0;
  /**
   * @NOTE:- Returns 2 if all submissions have a count between 1 and 8, otherwise returns 0.
   */
  return submission.every((s) => s.count > 0 && s.count <= 8) ? 2 : 0;
}

export function getProgressBarChartOptions(
  stages: SubmissionStageView[],
  total: number,
): ProgressBarOption {
  const _visibleStages = stages.filter((stage) => stage.count > 0);

  const _options: ProgressBarOption = {
    backgroundColor: '#ffffff',
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params: unknown) => {
        const _item = params as SubmissionStageChartTooltipEntry;
        const _label = _item.seriesName ?? 'Stage';
        const _count = Number(_item.data?.count ?? 0);
        const _color = _item.color ?? '#000000';
        return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${_color};margin-right:6px;"></span>${_label}: ${_count}`;
      },
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: total || 1,
      show: false,
      boundaryGap: [0, 0] as [number, number],
    },
    yAxis: {
      type: 'category',
      data: [''],
      show: false,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
    },
    grid: DEFAULT_SUBMISSION_CHART_GRID,
    series: _visibleStages.map((stage, index) => {
      const _percent = total > 0 ? (stage.count / total) * 100 : 0;
      const _displayCount =
        _percent < 2 ? Math.max(stage.count, total * 0.02) : stage.count;

      return {
        name: stage.label,
        type: 'bar',
        stack: 'submission-status',
        barWidth: '100%',
        barMinWidth: 2,
        barGap: '0%',
        barCategoryGap: '0%',
        data: [{ value: _displayCount, count: stage.count }],
        emphasis: {
          focus: 'series',
        },
        itemStyle: {
          color: stage.color,
          borderColor: '#ffffff',
          borderWidth: 2,
          borderRadius:
            index === 0 && _visibleStages.length === 1
              ? [10, 10, 10, 10]
              : index === 0
                ? [10, 0, 0, 10]
                : index === _visibleStages.length - 1
                  ? [0, 10, 10, 0]
                  : [0, 0, 0, 0],
        },
        label: {
          show: false,
        },
      };
    }),
  };

  return _options;
}

export function getDoughnutChartOptions(
  submissionDoughNutOption: SubmissionChartMapper,
  loadDefaultState = false,
): DoughnutOption {
  const _submissions = submissionDoughNutOption.data.map((submission) =>
    mapSubmissionStatusColor(submission, loadDefaultState),
  );
  const _chartType = submissionDoughNutOption.chartType;
  const _minAngle = calcMinAngle(submissionDoughNutOption.data);

  const _baseConfig = {
    tooltip: {
      trigger: 'item' as const,
      show: !loadDefaultState,
      confine: true,
    },
    legend: {
      top: '5%',
      left: 'center',
      show: false,
    },
  };

  return {
    ..._baseConfig,
    graphic: [
      buildSubmissionChartTextGraphic(
        submissionDoughNutOption.title,
        '42%',
        16,
        {
          ...DEFAULT_DONUT_TEXT_STYLE,
          fontWeight: 'bold',
        },
      ),
      buildSubmissionChartTextGraphic(
        submissionDoughNutOption.subtitle,
        '52%',
        10,
        {
          ...DEFAULT_DONUT_SUBTITLE_STYLE,
          fontWeight: 'normal',
        },
        1,
      ),
    ],
    series: [
      {
        name: submissionDoughNutOption.seriesName,
        type: _chartType,
        minAngle: _minAngle,
        radius: ['55%', '80%'],
        avoidLabelOverlap: false,
        padAngle: 2,
        itemStyle: {
          borderRadius: 2,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: false,
          position: 'center',
        },
        emphasis: {
          disabled: loadDefaultState,
          label: {
            show: false,
            fontSize: 40,
            fontWeight: 'bold',
          },
        },
        data: _submissions,
      },
    ],
  };
}

/**
 * @NOTE: OUTCOMES READY
 */

export function resolveOutcomeGroupDate(
  documents: readonly OutcomeDocument[],
): string | null {
  return documents.reduce<string | null>((latest, document) => {
    const _posted = document.posted_date;
    if (!_posted) {
      return latest;
    }
    if (latest === null) {
      return _posted;
    }
    return new Date(_posted) > new Date(latest) ? _posted : latest;
  }, null);
}

/**
 * Converts a transfer byte count into a whole-percent value for a progress ring.
 *
 * @param loaded - Bytes received so far.
 * @param total - Total bytes expected, or `undefined` when the server did not
 * expose `Content-Length`.
 * @returns A percentage clamped to 0-100, or `null` when the total is unknown
 * or non-positive — callers must render an indeterminate indicator for `null`
 * rather than substituting zero.
 *
 * @example
 * ```typescript
 * toDownloadPercent(512, 1024); // → 50
 * toDownloadPercent(512, undefined); // → null
 * ```
 */
export function toDownloadPercent(
  loaded: number,
  total: number | undefined,
): number | null {
  if (!total || total <= 0 || !Number.isFinite(total)) {
    return null;
  }

  const _percent = Math.round((loaded / total) * 100);

  return Math.min(100, Math.max(0, _percent));
}

/**
 * Estimates download progress from elapsed time alone.
 *
 * The companion to {@link toDownloadPercent}, used when that function returns
 * `null` because the response carried no readable `Content-Length` and
 * `HttpProgressEvent.total` was `undefined`. The curve is an ease-out —
 * `ceiling × (1 − (1 − t)²)` where `t` is the fraction of
 * `expectedDurationMs` elapsed — chosen over a straight line because it puts
 * most of the visible movement early, where the user is actually watching, and
 * eases into the ceiling instead of snapping.
 *
 * Reaches `ceiling` exactly at `expectedDurationMs` and holds there.
 *
 * @remarks The value is an estimate, not measured transfer data. Callers must
 * present it as such — the outcome download ring marks it "estimated" in its
 * accessible name — and must prefer a real {@link toDownloadPercent} result
 * whenever one is available.
 *
 * @param elapsedMs - Milliseconds since the transfer started. Values at or
 * below zero yield `0`.
 * @param expectedDurationMs - Time taken to travel the full range. Non-positive
 * or non-finite values yield `0`, since no meaningful curve can be shaped from
 * them.
 * @param ceiling - Value the curve climbs to and stops at. Defaults to
 * {@link OUTCOME_READY_PROGRESS_CEILING}.
 * @returns A whole percentage in the range `0 … ceiling`.
 *
 * @example
 * ```typescript
 * toElapsedPercent(0, 1200);     // → 0
 * toElapsedPercent(300, 1200);   // → 44  (quarter of the way, ease-out)
 * toElapsedPercent(600, 1200);   // → 75  (half the time, three-quarters done)
 * toElapsedPercent(1200, 1200);  // → 100
 * toElapsedPercent(9999, 1200);  // → 100 (held, never overshoots)
 * ```
 */
export function toElapsedPercent(
  elapsedMs: number,
  expectedDurationMs: number,
  ceiling: number = OUTCOME_READY_PROGRESS_CEILING,
): number {
  if (
    !Number.isFinite(elapsedMs) ||
    !Number.isFinite(expectedDurationMs) ||
    elapsedMs <= 0 ||
    expectedDurationMs <= 0
  ) {
    return 0;
  }

  const _fraction = Math.min(1, elapsedMs / expectedDurationMs);
  const _percent = Math.round(ceiling * (1 - (1 - _fraction) ** 2));

  return Math.min(ceiling, Math.max(0, _percent));
}

/**
 * Picks the percentage to report for an in-flight download, preferring
 * measured bytes and falling back to elapsed time.
 *
 * @param measuredPercent - Byte-derived percentage from
 * {@link toDownloadPercent}, or `null` when the response exposed no total.
 * @param elapsedMs - Milliseconds since the transfer started.
 * @param expectedDurationMs - Curve duration for the fallback estimate.
 * @param allowEstimate - When `false`, an absent measurement stays absent
 * instead of falling back to the clock.
 * @param ceiling - Upper bound for the fallback estimate. Defaults to
 * {@link OUTCOME_READY_PROGRESS_CEILING}.
 * @returns The reading and whether it came from bytes or from the clock.
 *
 * @example
 * ```typescript
 * readDownloadProgress(40, 600, 1200, true);   // → { value: 40, measured: true }
 * readDownloadProgress(null, 600, 1200, true); // → { value: 75, measured: false }
 * readDownloadProgress(null, 600, 1200, false); // → { value: null, measured: false }
 * ```
 */
export function readDownloadProgress(
  downloadProgressArgs: DownloadProgressArgs,
): DownloadProgressReading {
  const {
    measuredPercent,
    elapsedMs,
    expectedDurationMs,
    allowEstimate,
    ceiling,
  } = downloadProgressArgs;

  if (measuredPercent !== null) {
    return { value: measuredPercent, measured: true };
  }

  return {
    value: allowEstimate
      ? toElapsedPercent(elapsedMs, expectedDurationMs, ceiling)
      : null,
    measured: false,
  };
}

/**
 * Resolves the reading to display, given what is already on screen.
 *
 * Provenance travels with the value rather than being passed alongside it,
 * because the rules differ by direction and by which side is measured:
 *
 * - A first real byte count **supersedes** an estimate outright, even
 *   downwards. The estimate climbs to a full ceiling, so clamping the first
 *   measurement against it would hide every value the server ever sent.
 * - An estimate **never** overrides a measurement, in either direction. Once
 *   real data exists the clock is no longer evidence of anything, and an
 *   estimate sitting at its ceiling would otherwise show a finished ring on a
 *   transfer that has barely started.
 * - Between two readings of the same provenance the value never retreats — an
 *   indicator that goes backwards reads as a failure.
 *
 * @param next - The incoming reading.
 * @param previous - The reading currently displayed, or `null` when nothing has
 * been displayed yet.
 * @returns The reading to display, carrying the provenance of whichever value
 * won — so the next call can apply the rules above against what is actually on
 * screen.
 *
 * @example
 * ```typescript
 * const _estimate = { value: 75, measured: false };
 * resolveDisplayPercent({ value: 80, measured: false }, _estimate);  // → { value: 80, measured: false }  climbs
 * resolveDisplayPercent({ value: 60, measured: false }, _estimate);  // → { value: 75, measured: false }  clamped
 * resolveDisplayPercent({ value: 40, measured: true }, _estimate);   // → { value: 40, measured: true }   handover
 *
 * const _measured = { value: 40, measured: true };
 * resolveDisplayPercent({ value: 30, measured: true }, _measured);   // → { value: 40, measured: true }   clamped
 * resolveDisplayPercent({ value: 100, measured: false }, _measured); // → { value: 40, measured: true }   estimate ignored
 * ```
 */
export function resolveDisplayPercent(
  next: DownloadProgressReading,
  previous: DownloadProgressReading | null,
): DownloadProgressReading {
  if (next.value === null) {
    return { value: null, measured: false };
  }

  // `previous.value === null` rather than `!previous.value`: a displayed 0 is a
  // real reading, and treating it as "nothing shown yet" would route it to the
  // handover branch below — letting a ceiling-bound estimate replace a
  // measured 0% with a full ring.
  if (!previous || previous.value === null) {
    return next;
  }

  if (next.measured && !previous.measured) {
    return next;
  }

  if (!next.measured && previous.measured) {
    return previous;
  }

  return next.value > previous.value ? next : previous;
}

/**
 * Whether the elapsed-time estimate has reached its ceiling and can no longer
 * change.
 *
 * {@link toElapsedPercent} clamps at `expectedDurationMs`, so every tick past
 * that point recomputes an identical value. Callers driving the estimate from a
 * timer use this to stop ticking rather than keep paying for change detection
 * that cannot alter the output.
 *
 * @param elapsedMs - Milliseconds since the transfer started.
 * @param expectedDurationMs - Curve duration the estimate is shaped around.
 * @returns `true` when the estimate has settled and further ticks are wasted.
 *
 * @example
 * ```typescript
 * hasElapsedEstimateSettled(600, 1_200);   // → false (still climbing)
 * hasElapsedEstimateSettled(1_200, 1_200); // → true  (at the ceiling)
 * hasElapsedEstimateSettled(50, 0);        // → true  (degenerate duration, never climbs)
 * ```
 */
export function hasElapsedEstimateSettled(
  elapsedMs: number,
  expectedDurationMs: number,
): boolean {
  return (
    !Number.isFinite(expectedDurationMs) ||
    expectedDurationMs <= 0 ||
    elapsedMs >= expectedDurationMs
  );
}

/**
 * Maps a percentage onto a Material progress-spinner mode.
 *
 * @param percent - Percentage to render, or `null` when none is available.
 * @returns `'determinate'` when a value exists, `'indeterminate'` otherwise.
 *
 * @example
 * ```typescript
 * toSpinnerMode(42);   // → 'determinate'
 * toSpinnerMode(null); // → 'indeterminate'
 * ```
 */
export function toSpinnerMode(
  percent: number | null,
): 'determinate' | 'indeterminate' {
  return percent === null ? 'indeterminate' : 'determinate';
}

/**
 * Builds the accessible name for a download progress indicator.
 *
 * A bare progressbar announces neither which file is transferring nor how far
 * along it is. An elapsed-time value is marked "estimated" so assistive
 * technology never reports a byte count that was never observed.
 *
 * @param label - Caller's description, e.g. `'Downloading report.pdf'`.
 * @param percent - Percentage being displayed, or `null` when none.
 * @param measured - Whether that percentage came from measured bytes.
 * @returns The accessible name.
 *
 * @example
 * ```typescript
 * buildDownloadAriaLabel('Downloading', 40, true);  // → 'Downloading 40 percent'
 * buildDownloadAriaLabel('Downloading', 40, false); // → 'Downloading estimated 40 percent'
 * buildDownloadAriaLabel('Downloading', null, false); // → 'Downloading'
 * ```
 */
export function buildDownloadAriaLabel(
  label: string,
  percent: number | null,
  measured: boolean,
): string {
  if (percent === null) {
    return label;
  }

  return `${label} ${measured ? '' : 'estimated '}${percent} percent`;
}

/**
 * Classifies a failed download's HTTP status into an actionable failure reason.
 *
 * @param httpStatus - Status from the failed response, or `null` when the
 * request never reached the server.
 * @returns The failure class driving both the toast copy and whether the card
 * re-fetches its payload.
 *
 * @example
 * ```typescript
 * classifyDownloadFailure(403); // → 'access'
 * classifyDownloadFailure(0);   // → 'network'
 * ```
 */
export function classifyDownloadFailure(
  httpStatus: number | null,
): OutcomeDownloadFailureReason {
  switch (httpStatus) {
    case null:
      return 'unknown';
    case 0:
      return 'network';
    case 401:
    case 403:
    case 404:
      return 'access';
    default:
      // 5xx is a range rather than an enumerable set, so it stays a guard.
      return httpStatus >= 500 ? 'server' : 'unknown';
  }
}

/**
 * Narrows an `'access'` failure to the subset that really means "this document
 * is not yours any more", as opposed to "you are not signed in".
 *
 * {@link classifyDownloadFailure} deliberately groups 401 with 403/404 — from a
 * single document's point of view all three mean "you cannot have this right
 * now", and `OUTCOME_READY_TOAST.noLongerAvailable` is documented against all
 * three. That holds on the `HttpClient` path, where `AuthInterceptor` catches a
 * 401 first, refreshes the token, retries, and logs the user out if the refresh
 * fails — so a 401 never reaches the classifier as a settled answer.
 *
 * ERM-13269: the zipped group path has no such protection. It downloads through
 * a Web Worker using raw `fetch` with a token snapshot taken when the archive
 * started, so **no Angular interceptor runs at all**. A token expiring midway
 * through a large archive surfaces as a bare 401, and telling that user "This
 * document is no longer available. You may no longer have access to it." is
 * simply false — the documents are fine and the session is not. Group copy
 * therefore keys on revoked access only, and a purely-401 group falls through
 * to the retryable copy, which is both true and actionable once the session is
 * renewed.
 *
 * @param httpStatus - Status from the failed response, or `null`.
 * @returns True when the status means access was revoked rather than absent.
 *
 * @example
 * ```typescript
 * isRevokedAccessStatus(403); // → true
 * isRevokedAccessStatus(401); // → false (expired session, not revoked access)
 * ```
 */
export function isRevokedAccessStatus(httpStatus: number | null): boolean {
  return httpStatus === 403 || httpStatus === 404;
}

/**
 * True when a settled download failed for any access-class reason (401/403/404).
 *
 * This is the broad question — "might this row be stale?" — and is what drives
 * a payload re-fetch. Use {@link isRevokedFailure} to decide user-facing copy.
 *
 * @param outcome - A settled download outcome.
 * @returns Whether the outcome is an access-class failure.
 *
 * @example
 * ```typescript
 * const stale = results.some((r) => isAccessFailure(r.outcome));
 * ```
 */
export function isAccessFailure(outcome: OutcomeDownloadOutcome): boolean {
  return outcome.status === 'failure' && outcome.reason === 'access';
}

/**
 * True when a settled download failed specifically because access was revoked.
 *
 * The narrow question — "did they lose access to it?" — and the one that may
 * be stated to the user. Excludes 401, which means the session expired rather
 * than the document being taken away; see {@link isRevokedAccessStatus}.
 *
 * @param outcome - A settled download outcome.
 * @returns Whether the outcome is a revoked-access failure.
 *
 * @example
 * ```typescript
 * const allGone = failures.every((r) => isRevokedFailure(r.outcome));
 * ```
 */
export function isRevokedFailure(outcome: OutcomeDownloadOutcome): boolean {
  return (
    outcome.status === 'failure' && isRevokedAccessStatus(outcome.httpStatus)
  );
}

/**
 * Builds the archive file name for a zipped submission-group download.
 *
 * @param submissionName - Name of the submission the documents belong to.
 * @returns A filesystem-safe `.zip` name.
 *
 * @example
 * ```typescript
 * buildOutcomeZipFileName('Initial Review: Site 12'); // → 'Initial_Review_Site_12_outcomes.zip'
 * ```
 */
export function buildOutcomeZipFileName(submissionName: string): string {
  const _safe = (submissionName || '')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  return `${_safe || 'submission'}_outcomes.zip`;
}

/**
 * @NOTE VALIDATE PINNED ITEMS MAX COUNT
 */

export function validatePinnedItemsMaxCount(
  pinnedItems: DashboardPinnedItem | null,
  currentPinnedId: string,
): boolean {
  const _pinnedIds = new Set(pinnedItems?.items?.map((item) => item.entity.id));
  return !!(
    pinnedItems &&
    pinnedItems.count >= 10 &&
    !_pinnedIds.has(currentPinnedId)
  );
}
